package uz.mint.frontend.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.*
import com.varabyte.kobweb.compose.ui.graphics.Color

/**
 * The design's iconography, transcribed from *Mint Landing.dc.html*.
 *
 * Path data is copied verbatim rather than rebuilt with the typed path DSL — the design is the
 * source of truth for these shapes, and a hand-translated curve is a silent visual regression.
 */
@Composable
fun LineIcon(
    size: Int,
    stroke: Color,
    strokeWidth: Number = 2,
    paths: List<String>,
    viewBox: Int = 24,
) {
    Svg(attrs = { viewBox(0, 0, viewBox, viewBox); width(size); height(size) }) {
        paths.forEach { data ->
            Path {
                d(data)
                fill(SVGFillType.None)
                stroke(stroke)
                strokeWidth(strokeWidth)
                strokeLineCap(SVGStrokeLineCap.Round)
                strokeLineJoin(SVGStrokeLineJoin.Round)
            }
        }
    }
}

@Composable
fun SearchIcon(size: Int = 17, color: Color) =
    LineIcon(size, color, 2.2, listOf("M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Z", "M16.5 16.5 21 21"))

@Composable
fun ChevronDownIcon(size: Int = 12, color: Color) =
    LineIcon(size, color, 3, listOf("M5 8l7 7 7-7"))

@Composable
fun ArrowRightIcon(size: Int = 22, color: Color) =
    LineIcon(size, color, 2.4, listOf("M5 12h14M13 6l6 6-6 6"))

@Composable
fun CloseIcon(size: Int = 20, color: Color) =
    LineIcon(size, color, 2.4, listOf("M5 5l14 14M19 5 5 19"))

@Composable
fun MoonIcon(size: Int = 19, color: Color) =
    LineIcon(size, color, 2, listOf("M21 12.8A8.5 8.5 0 1 1 11.2 3a7 7 0 1 0 9.8 9.8Z"))

@Composable
fun SunIcon(size: Int = 19, color: Color) = LineIcon(
    size, color, 2,
    listOf(
        "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
        "M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
    ),
)

@Composable
fun ZapIcon(size: Int = 26, color: Color) =
    LineIcon(size, color, 2, listOf("M13 2 3 14h9l-1 8 10-12h-9l1-8Z"))

@Composable
fun PhoneIcon(size: Int = 26, color: Color) =
    LineIcon(size, color, 2, listOf("M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z", "M9 18h6"))

@Composable
fun DollarIcon(size: Int = 26, color: Color) =
    LineIcon(size, color, 2, listOf("M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"))

@Composable
fun ChatIcon(size: Int = 26, color: Color) =
    LineIcon(size, color, 2, listOf("M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"))

@Composable
fun TelegramIcon(size: Int = 18, color: Color) =
    LineIcon(size, color, 2, listOf("M21.5 3.5 2.5 10.8l5.4 1.9 1.7 5.6 3.1-3.4 4.6 3.6 4.2-15Z"))

@Composable
fun InstagramIcon(size: Int = 18, color: Color) = LineIcon(
    size, color, 2,
    listOf(
        "M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Z",
        "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
        "M17.5 6.5h.01",
    ),
)

@Composable
fun UploadIcon(size: Int = 34, color: Color) =
    LineIcon(size, color, 1.8, listOf("M12 16V4M7 9l5-5 5 5", "M20 16v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3"))

/**
 * A QR glyph: three finder squares and a scatter of modules.
 *
 * Drawn rather than transcribed — the design has no QR icon, because it predates the account
 * pages having anything to show. Filled squares rather than [LineIcon]'s strokes: at 20px a
 * stroked QR reads as noise, and the finder corners are what make the shape recognisable.
 */
@Composable
fun QrIcon(size: Int = 20, color: Color) {
    Svg(attrs = { viewBox(0, 0, 24, 24); width(size); height(size) }) {
        // The three finder patterns, as rings: outer square minus a hole, drawn with the
        // even-odd rule so the middle stays open.
        listOf(2 to 2, 14 to 2, 2 to 14).forEach { (px, py) ->
            Path {
                d("M$px ${py}h8v8h-8z M${px + 2} ${py + 2}v4h4v-4z")
                fill(color)
                attr("fill-rule", "evenodd")
            }
        }
        // Loose modules in the data quadrant, so the glyph is not just three corners.
        listOf(14 to 14, 20 to 14, 14 to 20, 17 to 17, 20 to 20).forEach { (px, py) ->
            Rect(attrs = { x(px); y(py); width(3); height(3); fill(color) })
        }
    }
}

/** Flag chips for the language menu — plain bars, matching the design's inline SVGs. */
@Composable
fun FlagRu(width: Int = 22) = FlagBars(width, listOf(0xFFFFFF to 5.0, 0x0039A6 to 5.0, 0xD52B1E to 5.0))

@Composable
fun FlagUz(width: Int = 22) = FlagBars(
    width,
    listOf(0x0099B5 to 4.6, 0xFFFFFF to 5.8, 0x1EB53A to 4.6),
)

@Composable
private fun FlagBars(width: Int, bars: List<Pair<Int, Double>>) {
    Svg(attrs = { viewBox(0, 0, 22, 15); width(width); height((width * 15 / 22)) }) {
        var y = 0.0
        bars.forEach { (rgb, h) ->
            val top = y
            Rect(attrs = {
                x(0); y(top); width(22); height(h)
                fill(Color.rgb(rgb))
            })
            y += h
        }
    }
}

@Composable
fun FlagEn(width: Int = 22) {
    Svg(attrs = { viewBox(0, 0, 22, 15); width(width); height((width * 15 / 22)) }) {
        Rect(attrs = { x(0); y(0); width(22); height(15); fill(Color.rgb(0x012169)) })
        listOf(
            "M0 0l22 15M22 0L0 15" to (3.0 to 0xFFFFFF),
            "M0 0l22 15M22 0L0 15" to (1.6 to 0xC8102E),
            "M11 0v15M0 7.5h22" to (5.0 to 0xFFFFFF),
            "M11 0v15M0 7.5h22" to (3.0 to 0xC8102E),
        ).forEach { (data, style) ->
            Path {
                d(data)
                fill(SVGFillType.None)
                stroke(Color.rgb(style.second))
                strokeWidth(style.first)
            }
        }
    }
}
